import React, { useEffect, useState } from 'react';
import { useMutation, useQuery, useQueryClient, keepPreviousData } from '@tanstack/react-query';
import { toast } from 'react-hot-toast';
import { MagnifyingGlassIcon, PlusIcon } from '@heroicons/react/24/outline';
import api from '../../services/api';
import { Modal } from '../../components/ui/Modal';
import { badgeClass, Field, FormActions, formatMoney, inputClass, primaryButtonClass, secondaryButtonClass } from '../../components/ui/FormControls';
import { cn } from '../../lib/utils';
import { day, failed, localToday, tdClass, thClass, useComponents, useContractors, useShifts } from './hrShared';

interface Employee {
  id: string;
  code: string;
  name: string;
  employmentType: 'permanent' | 'contract';
  contractorId: string | null;
  contractorName: string | null;
  department: string | null;
  designation: string | null;
  shiftId: string | null;
  shiftName: string | null;
  joinedOn: string;
  leftOn: string | null;
  phone: string | null;
  wageBasis: 'monthly' | 'daily';
  basicRate: number;
  payAmounts: Record<string, number>;
  uan: string | null;
  esiNumber: string | null;
  pan: string | null;
  bankAccount: string | null;
  ifsc: string | null;
  isActive: boolean;
}

const EmployeeForm: React.FC<{ employee: Employee | null; onDone: () => void }> = ({ employee, onDone }) => {
  const queryClient = useQueryClient();
  const { data: shifts = [] } = useShifts();
  const { data: contractors = [] } = useContractors();
  const { data: components = [] } = useComponents();
  const fixed = components.filter((component) => component.method === 'fixed' && component.isActive);
  const [form, setForm] = useState({
    code: employee?.code ?? '', name: employee?.name ?? '', employmentType: employee?.employmentType ?? 'permanent', contractorId: employee?.contractorId ?? '', department: employee?.department ?? '',
    designation: employee?.designation ?? '', shiftId: employee?.shiftId ?? '', joinedOn: employee?.joinedOn ?? localToday(), leftOn: employee?.leftOn ?? '', phone: employee?.phone ?? '',
    wageBasis: employee?.wageBasis ?? 'monthly', basicRate: employee ? String(employee.basicRate) : '', uan: employee?.uan ?? '', esiNumber: employee?.esiNumber ?? '', pan: employee?.pan ?? '',
    bankAccount: employee?.bankAccount ?? '', ifsc: employee?.ifsc ?? '', isActive: employee?.isActive ?? true,
  });
  const [amounts, setAmounts] = useState<Record<string, string>>(Object.fromEntries(Object.entries(employee?.payAmounts ?? {}).map(([code, value]) => [code, String(value)])));
  const set = (key: keyof typeof form) => (event: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => setForm((previous) => ({ ...previous, [key]: event.target.value }));
  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...form,
        contractorId: form.employmentType === 'contract' ? form.contractorId || null : null,
        basicRate: Number(form.basicRate || 0),
        payAmounts: Object.fromEntries(Object.entries(amounts).filter(([, value]) => value !== '').map(([code, value]) => [code, Number(value)])),
      };
      return employee ? api.put(`/hr/employees/${employee.id}`, body) : api.post('/hr/employees', body);
    },
    onSuccess: () => {
      toast.success(employee ? 'Employee saved' : 'Employee added');
      queryClient.invalidateQueries({ queryKey: ['hr'] });
      onDone();
    },
    onError: failed('Could not save the employee'),
  });
  const permanent = form.employmentType === 'permanent';
  return (
    <form className="space-y-5" onSubmit={(event) => { event.preventDefault(); save.mutate(); }}>
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Code" htmlFor="em-code"><input id="em-code" value={form.code} onChange={(event) => setForm((p) => ({ ...p, code: event.target.value.toUpperCase() }))} maxLength={20} className={inputClass} /></Field>
        <Field label="Name" htmlFor="em-name" className="sm:col-span-2"><input id="em-name" value={form.name} onChange={set('name')} maxLength={100} className={inputClass} /></Field>
        <Field label="Employment" htmlFor="em-type">
          <select id="em-type" value={form.employmentType} onChange={set('employmentType')} className={inputClass}><option value="permanent">On the rolls</option><option value="contract">Contract worker</option></select>
        </Field>
        {!permanent && (
          <Field label="Contractor" htmlFor="em-contractor" className="sm:col-span-2">
            <select id="em-contractor" value={form.contractorId} onChange={set('contractorId')} className={inputClass}>
              <option value="">Choose the contractor</option>
              {contractors.filter((c) => c.isActive || c.id === form.contractorId).map((contractor) => (<option key={contractor.id} value={contractor.id}>{contractor.name}</option>))}
            </select>
          </Field>
        )}
        <Field label="Department" htmlFor="em-dept"><input id="em-dept" value={form.department} onChange={set('department')} maxLength={60} className={inputClass} /></Field>
        <Field label="Designation" htmlFor="em-desig"><input id="em-desig" value={form.designation} onChange={set('designation')} maxLength={60} className={inputClass} /></Field>
        <Field label="Shift" htmlFor="em-shift">
          <select id="em-shift" value={form.shiftId} onChange={set('shiftId')} className={inputClass}>
            <option value="">No shift</option>
            {shifts.filter((s) => s.isActive || s.id === form.shiftId).map((shift) => (<option key={shift.id} value={shift.id}>{shift.name} ({shift.startTime}–{shift.endTime})</option>))}
          </select>
        </Field>
        <Field label="Joined on" htmlFor="em-joined"><input id="em-joined" type="date" value={form.joinedOn} onChange={set('joinedOn')} className={inputClass} /></Field>
        <Field label="Left on" htmlFor="em-left" hint="Empty while they work here"><input id="em-left" type="date" value={form.leftOn} onChange={set('leftOn')} className={inputClass} /></Field>
        <Field label="Phone" htmlFor="em-phone"><input id="em-phone" value={form.phone} onChange={set('phone')} maxLength={20} className={inputClass} /></Field>
      </div>

      {permanent && (
        <div className="space-y-4 rounded-md bg-gray-50 p-4 ring-1 ring-gray-200">
          <h4 className="text-sm font-semibold text-gray-900">Pay</h4>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="Paid" htmlFor="em-basis">
              <select id="em-basis" value={form.wageBasis} onChange={set('wageBasis')} className={inputClass}><option value="monthly">Monthly salary</option><option value="daily">Daily wage</option></select>
            </Field>
            <Field label={form.wageBasis === 'monthly' ? 'Monthly basic (₹)' : 'Basic daily wage (₹)'} htmlFor="em-basic"><input id="em-basic" type="number" min={0} step="0.01" value={form.basicRate} onChange={set('basicRate')} className={inputClass} /></Field>
            {fixed.map((component) => (
              <Field key={component.code} label={`${component.name} (₹)`} htmlFor={`em-pay-${component.code}`} hint={`Default ${formatMoney(Number(component.value))}`}>
                <input id={`em-pay-${component.code}`} type="number" min={0} step="0.01" value={amounts[component.code] ?? ''} placeholder={String(Number(component.value))} onChange={(event) => setAmounts((p) => ({ ...p, [component.code]: event.target.value }))} className={inputClass} />
              </Field>
            ))}
          </div>
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
            <Field label="UAN" htmlFor="em-uan"><input id="em-uan" value={form.uan} onChange={set('uan')} maxLength={30} className={inputClass} /></Field>
            <Field label="ESI number" htmlFor="em-esi"><input id="em-esi" value={form.esiNumber} onChange={set('esiNumber')} maxLength={30} className={inputClass} /></Field>
            <Field label="PAN" htmlFor="em-pan"><input id="em-pan" value={form.pan} onChange={(event) => setForm((p) => ({ ...p, pan: event.target.value.toUpperCase() }))} maxLength={10} className={inputClass} /></Field>
            <Field label="Bank account" htmlFor="em-bank"><input id="em-bank" value={form.bankAccount} onChange={set('bankAccount')} maxLength={40} className={inputClass} /></Field>
            <Field label="IFSC" htmlFor="em-ifsc"><input id="em-ifsc" value={form.ifsc} onChange={(event) => setForm((p) => ({ ...p, ifsc: event.target.value.toUpperCase() }))} maxLength={11} className={inputClass} /></Field>
          </div>
        </div>
      )}
      <label className="flex items-center gap-2 text-sm text-gray-700"><input type="checkbox" checked={form.isActive} onChange={(event) => setForm((p) => ({ ...p, isActive: event.target.checked }))} className="h-4 w-4 rounded border-gray-300 text-indigo-600" /> Active</label>
      <FormActions>
        <button type="button" onClick={onDone} className={secondaryButtonClass}>Cancel</button>
        <button type="submit" disabled={save.isPending} className={primaryButtonClass}>{save.isPending ? 'Saving…' : employee ? 'Save Changes' : 'Add Employee'}</button>
      </FormActions>
    </form>
  );
};

const Employees: React.FC<{ canEdit: boolean }> = ({ canEdit }) => {
  const [searchInput, setSearchInput] = useState('');
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [page, setPage] = useState(1);
  const [editing, setEditing] = useState<Employee | null | 'new'>(null);
  useEffect(() => {
    const timer = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 300);
    return () => clearTimeout(timer);
  }, [searchInput]);
  const { data, isLoading } = useQuery<{ data: Employee[]; total: number; totalPages: number }>({
    queryKey: ['hr', 'employees', page, search, type],
    queryFn: async () => (await api.get('/hr/employees', { params: { page, limit: 25, ...(search ? { search } : {}), ...(type ? { employmentType: type } : {}) } })).data,
    placeholderData: keepPreviousData,
  });
  const rows = data?.data ?? [];
  return (
    <div className="overflow-hidden rounded-lg bg-white shadow-sm ring-1 ring-gray-200">
      <div className="flex flex-col gap-3 border-b border-gray-200 p-4 sm:flex-row sm:items-center">
        <div className="relative flex-1">
          <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
          <input type="search" value={searchInput} onChange={(event) => setSearchInput(event.target.value)} placeholder="Search by code, name, department or designation" aria-label="Search employees" className={cn(inputClass, 'pl-9')} />
        </div>
        <select value={type} onChange={(event) => { setType(event.target.value); setPage(1); }} aria-label="Employment" className={cn(inputClass, 'sm:w-48')}>
          <option value="">Everyone</option>
          <option value="permanent">On the rolls</option>
          <option value="contract">Contract workers</option>
        </select>
        {canEdit && (
          <button type="button" onClick={() => setEditing('new')} className={primaryButtonClass}>
            <PlusIcon className="-ml-0.5 mr-2 h-4 w-4" />
            Add Employee
          </button>
        )}
      </div>
      {isLoading ? <p className="p-8 text-center text-sm text-gray-500">Loading…</p> : rows.length === 0 ? <p className="p-8 text-center text-sm text-gray-500">{search || type ? 'Nobody matches.' : 'Nobody yet. Add the employees and contract workers.'}</p> : (
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50"><tr><th className={thClass}>Code</th><th className={thClass}>Name</th><th className={thClass}>Employment</th><th className={thClass}>Department</th><th className={thClass}>Shift</th><th className={thClass}>Joined</th><th className={thClass}>Pay</th><th className={thClass}><span className="sr-only">Edit</span></th></tr></thead>
            <tbody className="divide-y divide-gray-100">
              {rows.map((employee) => (
                <tr key={employee.id} data-testid="employee-row" className={cn(!employee.isActive && 'bg-gray-50 text-gray-400')}>
                  <td className={cn(tdClass, 'font-mono')}>{employee.code}</td>
                  <td className={tdClass}>{employee.name}{employee.designation ? <span className="block text-xs text-gray-500">{employee.designation}</span> : null}</td>
                  <td className={tdClass}>{employee.employmentType === 'contract' ? <span className={cn(badgeClass, 'bg-amber-50 text-amber-700')}>Contract · {employee.contractorName}</span> : 'On the rolls'}</td>
                  <td className={tdClass}>{employee.department ?? '—'}</td>
                  <td className={tdClass}>{employee.shiftName ?? '—'}</td>
                  <td className={tdClass}>{day(employee.joinedOn)}{employee.leftOn ? ` – ${day(employee.leftOn)}` : ''}</td>
                  <td className={tdClass}>{employee.employmentType === 'contract' ? 'Paid by the contractor' : `${formatMoney(employee.basicRate)} ${employee.wageBasis === 'monthly' ? 'a month' : 'a day'}`}</td>
                  <td className={cn(tdClass, 'text-right')}>{canEdit && <button type="button" onClick={() => setEditing(employee)} className="text-sm font-medium text-indigo-600">Edit</button>}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
      {data && data.totalPages > 1 && (
        <div className="flex items-center justify-between border-t border-gray-200 px-4 py-3 text-sm text-gray-600">
          <span>{data.total} in all</span>
          <span className="flex gap-2">
            <button type="button" disabled={page <= 1} onClick={() => setPage(page - 1)} className={secondaryButtonClass}>Previous</button>
            <button type="button" disabled={page >= data.totalPages} onClick={() => setPage(page + 1)} className={secondaryButtonClass}>Next</button>
          </span>
        </div>
      )}
      <Modal isOpen={editing !== null} onClose={() => setEditing(null)} title={editing && editing !== 'new' ? `Edit ${editing.name}` : 'Add Employee'} size="2xl">
        {editing !== null && <EmployeeForm employee={editing === 'new' ? null : editing} onDone={() => setEditing(null)} />}
      </Modal>
    </div>
  );
};

export default Employees;
